import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLocations } from '@/stores/locations';
import { notify } from '@/lib/notify';
import { useRole } from '@/stores/identity';
import { LocationStatusBadge } from '@/components/LocationStatusBadge';
import { LocationCalendarSettings } from '@/components/calendar/LocationCalendarSettings';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';
import { transitionLocation } from '@/lib/locations';
import type { Location, LocationStatus } from '@/lib/types';

/**
 * Single location: show details + allow edits + lifecycle transitions.
 */
export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const status = useLocations((s) => s.status);
  const items = useLocations((s) => s.items);
  const load = useLocations((s) => s.load);
  const update = useLocations((s) => s.update);
  const role = useRole();

  const loc = items.find((l) => l.id === id) ?? null;

  // Hook must be called every render — pass org id or null while loading.
  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(loc?.organization_id);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  if (status === 'idle' || status === 'loading') {
    return <Loading />;
  }
  if (!loc) {
    return (
      <div className="container mx-auto py-10 px-4 text-center space-y-2">
        <h1 className="text-xl font-semibold">Location not found</h1>
        <Link to="/locations" className="text-sm underline">
          Back to all locations
        </Link>
      </div>
    );
  }

  // Cross-org read-only: hook is called above pre-return. Mutations are
  // refused server-side; hide write surfaces so the UI tells the truth.
  const canManage =
    (role === 'owner' || role === 'manager') && !isReadOnlyCrossOrg;
  const canTransition = role === 'owner' && !isReadOnlyCrossOrg;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {isReadOnlyCrossOrg && (
          <CrossOrgReadOnlyBanner
            entityLabel="location"
            blockedActionsLabel="edits and lifecycle changes"
          />
        )}
        <Link to="/locations" className="text-sm text-muted-foreground underline">
          ← All locations
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {loc.name}
            </h1>
            <div className="flex items-center gap-2">
              <LocationStatusBadge status={loc.status} />
              {loc.opened_at && (
                <span className="text-xs text-muted-foreground">
                  opened {new Date(loc.opened_at).toLocaleDateString()}
                </span>
              )}
              {loc.closed_at && (
                <span className="text-xs text-muted-foreground">
                  closed {new Date(loc.closed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          {!isReadOnlyCrossOrg && (
            <Link
              to={`/locations/${loc.id}/hours`}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Manage hours
            </Link>
          )}
        </div>

        {canManage ? (
          <DetailEditForm location={loc} onSave={(patch) => update(loc.id, patch)} />
        ) : (
          <ReadOnlyView location={loc} />
        )}

        <LocationCalendarSettings location={loc} />

        {canTransition && (
          <TransitionPanel
            location={loc}
            onTransitioned={() => {
              // Re-load locations to pull the updated row.
              void load();
            }}
          />
        )}

        <div className="text-xs text-muted-foreground">
          Created {new Date(loc.created_at).toLocaleString()} · Last updated{' '}
          {new Date(loc.updated_at).toLocaleString()}
        </div>
      </div>
    </div>
  );

  function Loading() {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading location...
      </div>
    );
  }
}

// ----- detail edit form -----------------------------------------------------

function DetailEditForm({
  location,
  onSave,
}: {
  location: Location;
  onSave: (
    patch: Partial<
      Pick<Location, 'name' | 'address' | 'phone' | 'timezone' | 'shift_split_time'>
    >
  ) => Promise<Location>;
}) {
  const [name, setName] = useState(location.name);
  const [address, setAddress] = useState(location.address ?? '');
  const [phone, setPhone] = useState(location.phone ?? '');
  const [timezone, setTimezone] = useState(location.timezone ?? '');
  const [splitTime, setSplitTime] = useState(
    location.shift_split_time.slice(0, 5)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
        timezone: timezone || null,
        shift_split_time: splitTime ? `${splitTime}:00` : '12:00:00',
      });
      setSavedAt(new Date());
      notify.success('Saved');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-border bg-card p-6 space-y-4"
    >
      <h2 className="text-base font-medium">Details</h2>

      <Field label="Pharmacy name" htmlFor="name">
        <input
          id="name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Address" htmlFor="address">
        <input
          id="address"
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Phone" htmlFor="phone">
        <input
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClass}
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Timezone"
          htmlFor="timezone"
          hint="Empty = inherit org default."
        >
          <input
            id="timezone"
            type="text"
            placeholder="(inherit)"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field
          label="Shift split time"
          htmlFor="split-time"
          hint="Half-day boundary."
        >
          <input
            id="split-time"
            type="time"
            value={splitTime}
            onChange={(e) => setSplitTime(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {savedAt && !error && (
        <p className="text-xs text-muted-foreground">
          Saved at {savedAt.toLocaleTimeString()}.
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

// ----- read-only view (staff/manager who can't edit details) ---------------

function ReadOnlyView({ location }: { location: Location }) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-2 text-sm">
      <Row label="Address" value={location.address ?? '—'} />
      <Row label="Phone" value={location.phone ?? '—'} />
      <Row label="Timezone" value={location.timezone ?? 'inherits org default'} />
      <Row label="Shift split" value={location.shift_split_time.slice(0, 5)} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border last:border-0 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

// ----- transition panel (owner only) ---------------------------------------

const NEXT_ALLOWED: Record<LocationStatus, LocationStatus[]> = {
  draft: ['active'],
  active: ['suspended', 'closed'],
  suspended: ['active', 'closed'],
  closed: [],
};

const TRANSITION_LABEL: Record<LocationStatus, string> = {
  draft: 'Set to draft',
  active: 'Activate',
  suspended: 'Suspend',
  closed: 'Close',
};

function TransitionPanel({
  location,
  onTransitioned,
}: {
  location: Location;
  onTransitioned: () => void;
}) {
  const allowed = NEXT_ALLOWED[location.status];
  const [target, setTarget] = useState<LocationStatus | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (allowed.length === 0) {
    return null;
  }

  async function onConfirm() {
    if (!target) return;
    setError(null);
    if (!reason.trim()) {
      setError('A reason is required for status transitions.');
      return;
    }
    setSubmitting(true);
    try {
      await transitionLocation(location.id, target, reason.trim());
      onTransitioned();
      setTarget(null);
      setReason('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <h2 className="text-base font-medium">Status</h2>
      <p className="text-xs text-muted-foreground">
        Current status:{' '}
        <strong className="text-foreground">{location.status}</strong>. Allowed
        transitions are listed below — each requires a reason for the audit
        trail.
      </p>

      <div className="flex flex-wrap gap-2">
        {allowed.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTarget(s)}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40"
          >
            {TRANSITION_LABEL[s]}
          </button>
        ))}
      </div>

      {target && (
        <div className="space-y-3 rounded-md border border-border p-4">
          <p className="text-sm">
            Confirm: change status to <strong>{target}</strong>?
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required, recorded in audit log)"
            rows={2}
            className={inputClass}
          />
          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive"
            >
              {error}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setTarget(null);
                setReason('');
                setError(null);
              }}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Working...' : `Confirm: ${TRANSITION_LABEL[target]}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- shared field helper -------------------------------------------------

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
